export type SpeechOwnershipDecisionType = 'allow' | 'hold' | 'reject';
export type SpeechOwnershipState = 'unknown' | 'confirmed-local' | 'suspect-remote' | 'rejected';

export interface SpeechOwnershipDecision {
  type: SpeechOwnershipDecisionType;
  reason: string;
  shouldCancel?: boolean;
}

export interface SpeechOwnershipAudioSnapshot {
  localIds: Array<string | null | undefined>;
  audioLevels: Record<string, number | undefined>;
  activeSpeakers: string[];
  dominantSpeakerId: string | null;
  audioMuted?: boolean;
  hasAudioTrack?: boolean;
}

export interface SpeechOwnershipGuardConfig {
  localLookbackMs: number;
  remoteLookbackMs: number;
  minCandidateAgeToRejectMs: number;
  localActivityThreshold: number;
  localWeakActivityThreshold: number;
  remoteStrongThreshold: number;
  remoteToLocalRatio: number;
  localRatioFloor: number;
  remoteDominantMinMs: number;
  minSamplesToReject: number;
}

interface AudioSample {
  at: number;
  localLevel: number;
  remoteLevel: number;
  localActive: boolean;
  remoteActive: boolean;
  localDominant: boolean;
  remoteDominant: boolean;
}

interface RecentStats {
  sampleCount: number;
  localMax: number;
  remoteMax: number;
  localActive: boolean;
  remoteActive: boolean;
  localDominant: boolean;
  latestRemoteDominant: boolean;
  remoteDominantDurationMs: number;
}

export const DEFAULT_SPEECH_OWNERSHIP_GUARD_CONFIG: SpeechOwnershipGuardConfig = {
  localLookbackMs: 1000,
  remoteLookbackMs: 900,
  minCandidateAgeToRejectMs: 600,
  localActivityThreshold: 0.02,
  localWeakActivityThreshold: 0.006,
  remoteStrongThreshold: 0.05,
  remoteToLocalRatio: 2.5,
  localRatioFloor: 0.01,
  remoteDominantMinMs: 400,
  minSamplesToReject: 2,
};

function toIdSet(ids: Array<string | null | undefined>): Set<string> {
  return new Set(ids.map((id) => (id ?? '').trim()).filter(Boolean));
}

function maxLevel(values: number[]): number {
  return values.reduce((max, value) => Math.max(max, Number.isFinite(value) ? value : 0), 0);
}

export class SpeechOwnershipGuard {
  private readonly config: SpeechOwnershipGuardConfig;
  private samples: AudioSample[] = [];
  private candidateStartedAt: number | null = null;
  private state: SpeechOwnershipState = 'unknown';

  constructor(config: Partial<SpeechOwnershipGuardConfig> = {}) {
    this.config = { ...DEFAULT_SPEECH_OWNERSHIP_GUARD_CONFIG, ...config };
  }

  getState(): SpeechOwnershipState {
    return this.state;
  }

  resetCandidate(): void {
    this.candidateStartedAt = null;
    this.state = 'unknown';
  }

  observeAudioSnapshot(snapshot: SpeechOwnershipAudioSnapshot, now = Date.now()): void {
    const localIds = toIdSet(snapshot.localIds);
    const dominantSpeakerId = snapshot.dominantSpeakerId ?? '';
    const activeSpeakers = snapshot.activeSpeakers ?? [];

    const localLevel = maxLevel(
      [...localIds].map((id) => snapshot.audioLevels[id] ?? 0)
    );

    let remoteLevel = 0;
    Object.entries(snapshot.audioLevels).forEach(([id, level]) => {
      if (!localIds.has(id)) {
        remoteLevel = Math.max(remoteLevel, Number.isFinite(level) ? Number(level) : 0);
      }
    });

    const localActive = activeSpeakers.some((id) => localIds.has(id));
    const remoteActive = activeSpeakers.some((id) => id && !localIds.has(id));
    const localDominant = Boolean(dominantSpeakerId && localIds.has(dominantSpeakerId));
    const remoteDominant = Boolean(dominantSpeakerId && !localIds.has(dominantSpeakerId));

    this.samples.push({
      at: now,
      localLevel: snapshot.audioMuted || snapshot.hasAudioTrack === false ? 0 : localLevel,
      remoteLevel,
      localActive: !snapshot.audioMuted && snapshot.hasAudioTrack !== false && localActive,
      remoteActive,
      localDominant: !snapshot.audioMuted && snapshot.hasAudioTrack !== false && localDominant,
      remoteDominant,
    });

    const retentionMs = Math.max(this.config.localLookbackMs, this.config.remoteLookbackMs) * 3;
    this.samples = this.samples.filter((sample) => now - sample.at <= retentionMs);
  }

  decideInterim(text: string, now = Date.now()): SpeechOwnershipDecision {
    return this.decide(text, false, now);
  }

  decideFinal(text: string, now = Date.now()): SpeechOwnershipDecision {
    return this.decide(text, true, now);
  }

  private decide(text: string, isFinal: boolean, now: number): SpeechOwnershipDecision {
    if (!text.trim()) {
      return { type: 'allow', reason: 'empty-text' };
    }

    if (this.candidateStartedAt == null) {
      this.candidateStartedAt = now;
      this.state = this.state === 'rejected' ? 'rejected' : 'unknown';
    }

    const stats = this.getRecentStats(now);
    const candidateAge = now - this.candidateStartedAt;
    const hasStrongLocalEvidence = stats.localActive
      || stats.localDominant
      || stats.localMax >= this.config.localActivityThreshold;
    const hasWeakLocalEvidence = stats.localMax >= this.config.localWeakActivityThreshold;

    if (this.state === 'confirmed-local') {
      return { type: 'allow', reason: 'local-already-confirmed' };
    }

    if (hasStrongLocalEvidence) {
      this.state = 'confirmed-local';
      return { type: 'allow', reason: 'local-confirmed' };
    }

    if (this.state === 'rejected') {
      if (hasWeakLocalEvidence) {
        this.state = 'confirmed-local';
        this.candidateStartedAt = now;
        return { type: 'allow', reason: 'local-recovered-after-reject' };
      }
      return { type: 'reject', reason: 'ownership-already-rejected', shouldCancel: false };
    }

    if (stats.sampleCount < this.config.minSamplesToReject) {
      if (!isFinal && this.hasEarlyRemoteHint(stats)) {
        this.state = 'suspect-remote';
        return { type: 'hold', reason: 'remote-speaker-pending' };
      }
      return { type: 'allow', reason: 'insufficient-audio-samples' };
    }

    if (hasWeakLocalEvidence) {
      return { type: 'allow', reason: 'local-weak-evidence' };
    }

    const remoteSuspicious = this.isRemoteSuspicious(stats);

    if (remoteSuspicious && candidateAge < this.config.minCandidateAgeToRejectMs) {
      return isFinal
        ? { type: 'allow', reason: 'final-before-reject-window' }
        : { type: 'hold', reason: 'remote-speaker-pending' };
    }

    if (remoteSuspicious) {
      this.state = 'rejected';
      return { type: 'reject', reason: 'remote-speaker', shouldCancel: true };
    }

    return { type: 'allow', reason: 'no-strong-remote-evidence' };
  }

  private getRecentStats(now: number): RecentStats {
    const localSamples = this.samples.filter((sample) => now - sample.at <= this.config.localLookbackMs);
    const remoteSamples = this.samples.filter((sample) => now - sample.at <= this.config.remoteLookbackMs);
    const latestSample = remoteSamples[remoteSamples.length - 1];

    let remoteDominantSince: number | null = null;
    for (let i = remoteSamples.length - 1; i >= 0; i--) {
      if (!remoteSamples[i].remoteDominant) break;
      remoteDominantSince = remoteSamples[i].at;
    }

    return {
      sampleCount: remoteSamples.length,
      localMax: maxLevel(localSamples.map((sample) => sample.localLevel)),
      remoteMax: maxLevel(remoteSamples.map((sample) => sample.remoteLevel)),
      localActive: localSamples.some((sample) => sample.localActive),
      remoteActive: remoteSamples.some((sample) => sample.remoteActive),
      localDominant: localSamples.some((sample) => sample.localDominant),
      latestRemoteDominant: latestSample?.remoteDominant ?? false,
      remoteDominantDurationMs: remoteDominantSince == null ? 0 : now - remoteDominantSince,
    };
  }

  private isRemoteSuspicious(stats: RecentStats): boolean {
    const remoteDominantStable = stats.latestRemoteDominant
      && stats.remoteDominantDurationMs >= this.config.remoteDominantMinMs;
    const remoteClearlyLouder = stats.remoteMax >= this.config.remoteStrongThreshold
      && stats.remoteMax >= Math.max(stats.localMax, this.config.localRatioFloor) * this.config.remoteToLocalRatio;

    return remoteClearlyLouder
      && remoteDominantStable
      && stats.remoteActive
      && !stats.localActive
      && !stats.localDominant;
  }

  private hasEarlyRemoteHint(stats: RecentStats): boolean {
    const remoteClearlyLouder = stats.remoteMax >= this.config.remoteStrongThreshold
      && stats.remoteMax >= Math.max(stats.localMax, this.config.localRatioFloor) * this.config.remoteToLocalRatio;

    return remoteClearlyLouder
      && stats.latestRemoteDominant
      && stats.remoteActive
      && !stats.localActive
      && !stats.localDominant;
  }
}